import "@fontsource/montserrat/latin-400.css";
import "@fontsource/montserrat/latin-600.css";
import "@fontsource/playfair-display/600.css";
import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { IconArrowLeft } from "@tabler/icons-react";
import { Box, Center, Loader, LoadingOverlay, Text } from "@mantine/core";
import { QuoteContext } from "@/features/form/context/QuoteContext";
import { PaymentForm } from "@/features/form/components/PaymentForm";
import { buildProductQuote, createQuote } from "@/services/quote";
import { BuildProductQuoteResponse, CreateQuoteRequest } from "@/types/api/quote";
import { readStoreCart } from "../cart";
import styles from "./StoreCheckoutPage.module.scss";

export function StoreCheckoutPage() {
  const navigate = useNavigate();
  const { setQuoteItems, quoteId, setQuoteId, setPlanDurationDays } = useContext(QuoteContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  // Separate from isSaving: PaymentForm must stay mounted while its own requests run, or it loses its step and selections.
  const [isPaymentFormBusy, setIsPaymentFormBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function prepareCheckout() {
      setPlanDurationDays(0);
      setQuoteId(0);
      const cart = readStoreCart();
      if (!cart.length) {
        setError("Seu carrinho está vazio. Volte à loja para escolher seus produtos.");
        setLoading(false);
        return;
      }
      try {
        const previews: BuildProductQuoteResponse[] = [];
        const productsAndGroups: CreateQuoteRequest["productsAndGroups"] = [];
        for (const line of cart) {
          const quotes = await buildProductQuote({ productIds: line.productIds, planDurationDays: 0 });
          if (cancelled) return;
          const quotedProductIds = new Set(quotes.flatMap((quote) =>
            quote.type === "product"
              ? quote.product ? [quote.product.productId] : []
              : quote.group?.products.map((product) => product.productId) ?? [],
          ));
          const requestedIds = new Set(line.productIds);
          const matchesProducts = quotedProductIds.size === requestedIds.size &&
            [...requestedIds].every((id) => quotedProductIds.has(id));
          const matchesGroup = line.type !== "group" || quotes.some((quote) =>
            quote.type === "group" && quote.group?.groupId === line.id,
          );
          if (!matchesProducts || !matchesGroup) {
            throw new Error("Os itens do carrinho mudaram ou não estão mais disponíveis. Volte à loja e atualize seu carrinho.");
          }
          previews.push(...quotes);
          if (line.type === "group") {
            productsAndGroups.push({
              type: "group",
              group: { id: line.id, products: line.productIds.map((id) => ({ id, quantity: 1 })) },
            });
          } else {
            productsAndGroups.push({ type: "product", product: { id: line.id, quantity: 1 } });
          }
        }
        if (!cancelled) {
          setQuoteItems(previews);
          setLoading(false);
          setIsSaving(true);
        }
        if (cancelled) return;
        const quote = await createQuote({ planDurationDays: 0, productsAndGroups });
        if (!cancelled) {
          setQuoteId(quote.quoteId);
          setIsSaving(false);
        }
      } catch (cause) {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : "Não foi possível preparar seu pedido. Tente novamente.");
          setLoading(false);
          setIsSaving(false);
        }
      }
    }
    void prepareCheckout();
    return () => { cancelled = true; };
    // Quote is created once when entering checkout. Context setters are stable React setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <main className={styles.page}>
    <header className={styles.header}><Link to="/" className={styles.back}><IconArrowLeft size={18} /> Voltar à loja</Link><span>Checkout Vinci</span></header>
    <div className={styles.content}>
      {(loading || isSaving) && <Center className={styles.status}><Box ta="center"><Loader color="dark" /><Text mt="md">{loading ? "Preparando seu pedido…" : "Salvando sua cotação segura…"}</Text></Box></Center>}
      {error && <section className={styles.error}><h1>Não foi possível continuar</h1><p>{error}</p><Link to="/">Voltar à loja</Link></section>}
      {!loading && !isSaving && !error && quoteId > 0 && <>
        <LoadingOverlay visible={isPaymentFormBusy} pos="fixed" />
        <PaymentForm handleBackToQuote={() => navigate("/")} setIsLoading={setIsPaymentFormBusy} />
      </>}
    </div>
  </main>;
}
